import { useEffect, useId, useRef, useState } from 'react';
import { Info } from 'lucide-react';

import { Tooltip } from '@/components/Tooltip/Tooltip';
import { MathText } from '@/features/content/MathText';
import {
  DEFAULT_TOLERANCE_FRACTION,
  INSTRUCTOR_TOLERANCE_HELP,
  describeToleranceRange,
  percentToTolerance,
  toleranceToPercent,
} from '@/features/questions/numericalTolerance';

import { FORMULA_NAMES_HINT } from './questionDialogUtils';
import styles from './NumericalAnswerEditor.module.css';

interface NumericalAnswerEditorProps {
  answerUnits?: string;
  disabled?: boolean;
  numericalAnswer?: number;
  numericalError?: number;
  structuralEditsRestricted?: boolean;
  useRandomVariables?: boolean;
  variableBasedAnswer?: string;
  onChange: (updates: {
    answerUnits?: string;
    numericalAnswer?: number;
    numericalError?: number;
    variableBasedAnswer?: string;
  }) => void;
}

function toPercentText(fraction: number | undefined) {
  return fraction === undefined ? '' : String(toleranceToPercent(fraction));
}

export function NumericalAnswerEditor({
  answerUnits,
  disabled = false,
  numericalAnswer,
  numericalError,
  useRandomVariables = false,
  variableBasedAnswer,
  onChange,
}: NumericalAnswerEditorProps) {
  // The field is a percentage while the stored value is a fraction, so what the
  // instructor is typing is held here as TEXT. Deriving the field from the prop
  // on every render would rewrite "5." to "5" mid-keystroke, and would round
  // half-typed values under the caret.
  const [percentText, setPercentText] = useState(() =>
    toPercentText(numericalError ?? DEFAULT_TOLERANCE_FRACTION),
  );
  const toleranceId = useId();
  const formulaId = useId();
  const formulaHintId = useId();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // A question with no stored tolerance gets the default, and it is pushed up
  // rather than only shown: a tolerance is required to save, so a field that
  // displayed 5% without supplying it would fail validation while looking
  // filled in.
  useEffect(() => {
    if (numericalError === undefined) {
      onChangeRef.current({ numericalError: DEFAULT_TOLERANCE_FRACTION });
    }
    // Deliberately on mount only: this seeds a missing value, it does not track
    // the prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-sync only when the stored value is not what this field is already
  // showing — which happens when the draft is reset from outside, and not when
  // the instructor is part-way through typing.
  useEffect(() => {
    if (numericalError === undefined) {
      return;
    }

    if (percentToTolerance(Number(percentText)) === numericalError) {
      return;
    }

    setPercentText(toPercentText(numericalError));
    // percentText is deliberately not a dependency: this compares against it,
    // it does not react to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericalError]);

  const toleranceFraction = numericalError ?? DEFAULT_TOLERANCE_FRACTION;
  const acceptedRange = useRandomVariables
    ? undefined
    : describeToleranceRange(toleranceFraction, numericalAnswer);

  return (
    <div className={styles.root}>
      {useRandomVariables ? (
        <div className={styles.answerField}>
          <label className={styles.label} htmlFor={formulaId}>
            Variable-Based Answer
          </label>
          <input
            aria-describedby={formulaHintId}
            className={styles.input}
            disabled={disabled}
            id={formulaId}
            onChange={(event) => {
              onChange({
                variableBasedAnswer: event.target.value,
              });
            }}
            type="text"
            value={variableBasedAnswer ?? ''}
          />
          <p className={styles.formulaHint} id={formulaHintId}>
            {FORMULA_NAMES_HINT}
          </p>
        </div>
      ) : (
        <label className={styles.answerField}>
          <span className={styles.label}>Correct Answer</span>
          <input
            className={styles.input}
            disabled={disabled}
            onChange={(event) => {
              onChange({
                numericalAnswer:
                  event.target.value === '' ? undefined : Number(event.target.value),
              });
            }}
            step="any"
            type="number"
            value={numericalAnswer ?? ''}
          />
        </label>
      )}

      {/* Answer and tolerance share a row: they are one decision — the value and
          how close counts — and splitting them across two lines made the reader
          scroll to see both. */}
      <div className={styles.inlineFields}>
        {/* NOT a wrapping <label>: a <button> is a labelable element, so the
            info trigger would become the label's target and the input would be
            left unlabelled. Explicit htmlFor/id instead. */}
        <div className={styles.compactField}>
          <span className={styles.label}>
            <label htmlFor={toleranceId}>Error Tolerance</label>
            <Tooltip content={INSTRUCTOR_TOLERANCE_HELP}>
              <button
                aria-label="What is error tolerance?"
                className={styles.infoButton}
                type="button"
              >
                <Info aria-hidden="true" size={14} strokeWidth={2} />
              </button>
            </Tooltip>
          </span>
          <div className={styles.percentInput}>
            <input
              className={styles.input}
              disabled={disabled}
              id={toleranceId}
              min="0"
              onChange={(event) => {
                const nextText = event.target.value;
                setPercentText(nextText);
                onChange({
                  numericalError:
                    nextText === ''
                      ? undefined
                      : percentToTolerance(Number(nextText)),
                });
              }}
              step="any"
              type="number"
              value={percentText}
            />
            <span aria-hidden="true" className={styles.percentSuffix}>
              %
            </span>
          </div>
        </div>

        <label className={styles.compactField}>
          <span className={styles.label}>Answer Units</span>
          <div className={styles.unitsRow}>
            <input
              className={styles.input}
              disabled={disabled}
              onChange={(event) => {
                onChange({
                  answerUnits: event.target.value,
                });
              }}
              placeholder="m/s"
              type="text"
              value={answerUnits ?? ''}
            />
            {/* Rendered beside the field, not under it: units are usually LaTeX
                and the point of the preview is to check it as you type. */}
            {answerUnits?.trim() ? (
              <span className={styles.unitsPreview}>
                <MathText text={answerUnits} />
              </span>
            ) : null}
          </div>
        </label>
      </div>

      {acceptedRange ? (
        // The abstract percentage made concrete against the answer actually
        // being authored — this is what an instructor is really deciding.
        <p className={styles.toleranceRange}>Accepts {acceptedRange}</p>
      ) : null}
    </div>
  );
}
